// hardcoded-ui-text: allow — a developer-only panel: RealtimeChat.tsx mounts it behind `showDebug` and NODE_ENV === 'development'.
// It is not part of the product a customer or an operator sees, so its copy stays in
// English. Checked at the call site, not assumed from the folder name.
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface AIAnimationTestProps {
  conversationId?: string;
  tenantSchema?: string;
}

export function AIAnimationTest({ conversationId, tenantSchema }: AIAnimationTestProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const testAIAnimation = async () => {
    if (!conversationId || !tenantSchema) {
      toast({
        title: "Missing data",
        description: "Need conversation ID and tenant schema to test",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      const broadcastChannel = `tenant_${tenantSchema}`;
      console.log(`[AI Test] Sending test broadcast to channel: ${broadcastChannel}`);
      
      const result = await supabase.channel(broadcastChannel).send({
        type: 'broadcast',
        event: 'ai_processing_start',
        payload: {
          conversationId,
          customerId: 'test-customer',
          customerName: 'Test User',
          timestamp: new Date().toISOString()
        }
      });
      
      console.log('[AI Test] Broadcast result:', result);
      
      toast({
        title: "Test broadcast sent",
        description: `Sent to channel: ${broadcastChannel}`,
      });
      
      // Auto-stop after 3 seconds for testing
      setTimeout(async () => {
        await supabase.channel(broadcastChannel).send({
          type: 'broadcast',
          event: 'ai_processing_stop',
          payload: {
            conversationId,
            timestamp: new Date().toISOString()
          }
        });
        console.log('[AI Test] Sent stop broadcast');
      }, 3000);
      
    } catch (error) {
      console.error('[AI Test] Failed to send broadcast:', error);
      toast({
        title: "Test failed",
        description: `Error: ${error}`,
        variant: "destructive"
      });
    }
    setIsLoading(false);
  };

  return (
    <Button 
      onClick={testAIAnimation}
      disabled={isLoading || !conversationId || !tenantSchema}
      variant="outline"
      size="sm"
    >
      {isLoading ? 'Testing...' : 'Test AI Animation'}
    </Button>
  );
}